/**
 * TitanSwapProvider - Titan Exchange WebSocket implementation
 *
 * Uses Titan's WebSocket API for real-time streaming quotes.
 * https://docs.triton.one/trading-apis/titan-swap-api
 * https://titan-exchange.gitbook.io/titan/titan-developer-docs/apis/swap-api
 *
 * Note: This implementation requires the RPC endpoint to include Titan access.
 * The endpoint format is: wss://<your-endpoint>.rpcpool.com/<your-token>/titan/api/v1/ws
 */

import { SwapProvider, SwapQuote, SwapRouteStep } from './SwapProvider.js';
import { Connection, VersionedTransaction } from '@solana/web3.js';

// MessagePack encoder/decoder
// We use a minimal implementation for browser compatibility
// In production, consider using @msgpack/msgpack or similar

const PROTOCOL_VERSION = 'v1.api.titan.ag';

export class TitanSwapProvider extends SwapProvider {
    constructor(config = {}) {
        super(config);
        this.name = 'titan';
        this.displayName = 'Titan Exchange';
        this.logo = '/images/vendor/titan-logo.svg';

        // Configuration
        // Convert HTTPS RPC endpoint to WebSocket Titan endpoint
        this.rpcEndpoint = config.rpcEndpoint || 'https://solanac-solana-c8d3.mainnet.rpcpool.com/39701859-d2f9-40e5-93bb-491f5a4791e1';
        this.wsEndpoint = this._buildWsEndpoint(config.rpcEndpoint || this.rpcEndpoint);
        this.authToken = config.authToken || null;

        // Solana connection for executing swaps
        this.connection = null;

        // WebSocket state
        this._ws = null;
        this._streamId = null;
        this._requestId = 0;
        this._pendingRequests = new Map();
        this._reconnectAttempts = 0;
        this._maxReconnectAttempts = 3;
        this._reconnectDelay = 1000;
        this._lastParams = null;

        // MessagePack library reference (loaded dynamically)
        this._msgpack = null;
    }

    /**
     * Build WebSocket endpoint from RPC endpoint
     */
    _buildWsEndpoint(rpcEndpoint) {
        // Extract the base URL and token from the RPC endpoint
        // Example: https://solanac-solana-c8d3.mainnet.rpcpool.com/39701859-d2f9-40e5-93bb-491f5a4791e1
        // Becomes: wss://solanac-solana-c8d3.mainnet.rpcpool.com/39701859-d2f9-40e5-93bb-491f5a4791e1/titan/api/v1/ws

        let url = rpcEndpoint
            .replace('https://', 'wss://')
            .replace('http://', 'ws://');

        // Remove trailing slash if present
        url = url.replace(/\/$/, '');

        // Add Titan API path
        return `${url}/titan/api/v1/ws`;
    }

    /**
     * Load MessagePack library dynamically
     */
    async _loadMsgpack() {
        if (this._msgpack) return this._msgpack;

        // Try to use native ES module import for @msgpack/msgpack
        // This works if the package is installed via npm
        try {
            const msgpack = await import('@msgpack/msgpack');
            this._msgpack = {
                encode: msgpack.encode,
                decode: msgpack.decode
            };
            return this._msgpack;
        } catch (e) {
            // Fallback: Use a simple JSON-based encoding for development
            // Note: Production should use proper MessagePack
            console.warn('MessagePack not available, using JSON fallback. Install @msgpack/msgpack for full Titan support.');
            this._msgpack = {
                encode: (data) => new TextEncoder().encode(JSON.stringify(data)),
                decode: (data) => JSON.parse(new TextDecoder().decode(data))
            };
            return this._msgpack;
        }
    }

    /**
     * Generate unique request ID
     */
    _nextRequestId() {
        return ++this._requestId;
    }

    /**
     * Convert public key to Uint8Array for Titan API
     */
    _pubkeyToBytes(pubkeyString) {
        // Import bs58 for decoding
        // For browser compatibility, we use a simple base58 decoder
        const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
        const ALPHABET_MAP = {};
        for (let i = 0; i < ALPHABET.length; i++) {
            ALPHABET_MAP[ALPHABET.charAt(i)] = i;
        }

        let bytes = [];
        for (let i = 0; i < pubkeyString.length; i++) {
            let carry = ALPHABET_MAP[pubkeyString.charAt(i)];
            for (let j = 0; j < bytes.length; j++) {
                carry += bytes[j] * 58;
                bytes[j] = carry & 0xff;
                carry >>= 8;
            }
            while (carry > 0) {
                bytes.push(carry & 0xff);
                carry >>= 8;
            }
        }

        // Handle leading zeros
        for (let i = 0; i < pubkeyString.length && pubkeyString[i] === '1'; i++) {
            bytes.push(0);
        }

        return new Uint8Array(bytes.reverse());
    }

    /**
     * Convert bytes to base58 string
     */
    _bytesToPubkey(bytes) {
        const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

        let result = '';
        let digits = [0];

        for (let i = 0; i < bytes.length; i++) {
            let carry = bytes[i];
            for (let j = 0; j < digits.length; j++) {
                carry += digits[j] << 8;
                digits[j] = carry % 58;
                carry = Math.floor(carry / 58);
            }
            while (carry > 0) {
                digits.push(carry % 58);
                carry = Math.floor(carry / 58);
            }
        }

        // Handle leading zeros
        for (let i = 0; i < bytes.length && bytes[i] === 0; i++) {
            result += '1';
        }

        for (let i = digits.length - 1; i >= 0; i--) {
            result += ALPHABET[digits[i]];
        }

        return result;
    }

    async initialize() {
        try {
            this._emitStatus('connecting', 'Initializing Titan Exchange...');

            // Load MessagePack
            await this._loadMsgpack();

            // Create Solana connection
            this.connection = new Connection(this.rpcEndpoint, {
                commitment: 'confirmed',
                confirmTransactionInitialTimeout: 120000
            });

            // Connect WebSocket
            await this._connectWebSocket();

            return true;
        } catch (error) {
            this._emitError(error);
            this._emitStatus('error', error.message);
            return false;
        }
    }

    async _connectWebSocket() {
        return new Promise((resolve, reject) => {
            // Build URL with auth if provided
            let url = this.wsEndpoint;
            if (this.authToken) {
                url += `?auth=${this.authToken}`;
            }

            // Create WebSocket with protocol
            this._ws = new WebSocket(url, [PROTOCOL_VERSION]);
            this._ws.binaryType = 'arraybuffer';

            const timeout = setTimeout(() => {
                reject(new Error('WebSocket connection timeout'));
                this._ws.close();
            }, 10000);

            this._ws.onopen = () => {
                clearTimeout(timeout);
                this.isConnected = true;
                this._reconnectAttempts = 0;
                this._emitStatus('connected', 'Titan Exchange connected');
                resolve();
            };

            this._ws.onclose = (event) => {
                clearTimeout(timeout);
                this.isConnected = false;
                this._emitStatus('disconnected', `Connection closed: ${event.reason || event.code}`);

                // Attempt reconnect if we were streaming
                if (this.isStreaming && this._reconnectAttempts < this._maxReconnectAttempts) {
                    this._reconnectAttempts++;
                    setTimeout(() => this._reconnect(), this._reconnectDelay * this._reconnectAttempts);
                }
            };

            this._ws.onerror = (error) => {
                clearTimeout(timeout);
                this._emitError(new Error('WebSocket error'));
                this._emitStatus('error', 'WebSocket connection error');
                reject(error);
            };

            this._ws.onmessage = (event) => this._handleMessage(event);
        });
    }

    async _reconnect() {
        this._emitStatus('reconnecting', `Reconnecting... (attempt ${this._reconnectAttempts})`);

        try {
            await this._connectWebSocket();

            // Resume streaming if we were before
            if (this._lastParams) {
                this.startQuoteStream(this._lastParams);
            }
        } catch (error) {
            this._emitError(error);
        }
    }

    async _handleMessage(event) {
        try {
            const data = this._msgpack.decode(new Uint8Array(event.data));

            // Handle different message types based on Titan protocol
            if (data.Response) {
                this._handleResponse(data.Response);
            } else if (data.Error) {
                this._handleError(data.Error);
            } else if (data.StreamData) {
                this._handleStreamData(data.StreamData);
            } else if (data.StreamEnd) {
                this._handleStreamEnd(data.StreamEnd);
            }
        } catch (error) {
            console.error('Error handling Titan message:', error);
            this._emitError(error);
        }
    }

    _handleResponse(response) {
        // Match response to pending request
        if (response.id && this._pendingRequests.has(response.id)) {
            const { resolve, reject } = this._pendingRequests.get(response.id);
            this._pendingRequests.delete(response.id);

            if (response.result) {
                resolve(response.result);
            } else {
                reject(new Error(response.error || 'Unknown error'));
            }
        }
    }

    _handleError(error) {
        const err = new Error(error.message || 'Titan API error');
        err.code = error.code;
        this._emitError(err);
    }

    _handleStreamData(streamData) {
        // Parse quotes from stream data
        if (streamData.quotes) {
            const quotes = this._parseStreamQuotes(streamData.quotes);
            this._emitQuote(quotes);
        }
    }

    _handleStreamEnd(streamEnd) {
        this.isStreaming = false;
        this._streamId = null;
        this._emitStatus('stopped', 'Quote stream ended');
    }

    /**
     * Parse Titan stream quotes into SwapQuote objects
     */
    _parseStreamQuotes(titanQuotes) {
        const quotes = [];

        // Titan returns quotes as { [providerId]: SwapRoute }
        for (const [providerId, route] of Object.entries(titanQuotes.quotes || {})) {
            const quote = this._parseTitanRoute(route, providerId, titanQuotes);
            quotes.push(quote);
        }

        return quotes;
    }

    /**
     * Parse a single Titan route into SwapQuote
     */
    _parseTitanRoute(route, providerId, quoteContext) {
        // Parse route steps
        const routeSteps = (route.steps || []).map(step => new SwapRouteStep({
            ammKey: step.ammKey ? this._bytesToPubkey(step.ammKey) : null,
            label: step.label || 'Unknown',
            inputMint: step.inputMint ? this._bytesToPubkey(step.inputMint) : null,
            outputMint: step.outputMint ? this._bytesToPubkey(step.outputMint) : null,
            inputAmount: step.inAmount || 0,
            outputAmount: step.outAmount || 0,
            feeAmount: step.feeAmount || 0,
            feeMint: step.feeMint ? this._bytesToPubkey(step.feeMint) : null
        }));

        // Generate route label
        const routeLabel = routeSteps.length > 0
            ? routeSteps.map(s => s.label).join(' → ')
            : `${providerId}`;

        return new SwapQuote({
            provider: `titan:${providerId}`,
            inputMint: quoteContext.inputMint ? this._bytesToPubkey(quoteContext.inputMint) : null,
            outputMint: quoteContext.outputMint ? this._bytesToPubkey(quoteContext.outputMint) : null,
            inputAmount: route.inAmount || 0,
            outputAmount: route.outAmount || 0,
            priceImpact: 0, // Titan doesn't directly provide this
            slippageBps: route.slippageBps || 0,
            route: routeSteps,
            transaction: route.transaction || null, // Pre-built transaction
            expiresAt: route.expiresAtMs || (Date.now() + 10000),
            computeUnits: route.computeUnits || null,
            platformFee: route.platformFee ? {
                amount: route.platformFee.amount,
                bps: route.platformFee.fee_bps
            } : null,
            routeLabel: routeLabel,
            raw: route
        });
    }

    /**
     * Send RPC request to Titan
     */
    async _sendRequest(method, params) {
        if (!this.isConnected || !this._ws) {
            throw new Error('Not connected to Titan');
        }

        const id = this._nextRequestId();

        return new Promise((resolve, reject) => {
            this._pendingRequests.set(id, { resolve, reject });

            const message = {
                jsonrpc: '2.0',
                id: id,
                method: method,
                params: params
            };

            const encoded = this._msgpack.encode(message);
            this._ws.send(encoded);

            // Timeout after 30 seconds
            setTimeout(() => {
                if (this._pendingRequests.has(id)) {
                    this._pendingRequests.delete(id);
                    reject(new Error('Request timeout'));
                }
            }, 30000);
        });
    }

    async getQuote(params) {
        if (!this.isConnected) {
            throw new Error('Titan provider not initialized');
        }

        this._emitStatus('fetching', 'Fetching Titan quote...');

        try {
            // For single quotes, use GetSwapPrice
            const result = await this._sendRequest('GetSwapPrice', {
                inputMint: this._pubkeyToBytes(params.inputMint),
                outputMint: this._pubkeyToBytes(params.outputMint),
                amount: params.amount,
                swapMode: params.swapMode === 'ExactOut' ? 'ExactOut' : 'ExactIn',
                slippageBps: params.slippageBps || 50,
                dexes: params.dexes || [],
                excludeDexes: params.excludeDexes || []
            });

            const quote = new SwapQuote({
                provider: 'titan',
                inputMint: params.inputMint,
                outputMint: params.outputMint,
                inputAmount: params.amount,
                outputAmount: result.amountOut,
                priceImpact: 0,
                slippageBps: params.slippageBps || 50,
                route: [],
                transaction: null,
                expiresAt: Date.now() + 10000,
                computeUnits: null,
                platformFee: null,
                routeLabel: 'Titan',
                raw: result
            });

            this._emitStatus('ready', 'Quote received');
            return [quote];

        } catch (error) {
            this._emitError(error);
            this._emitStatus('error', error.message);
            throw error;
        }
    }

    startQuoteStream(params) {
        if (!this.isConnected || !this._ws) {
            this._emitError(new Error('Not connected to Titan'));
            return false;
        }

        // Stop existing stream
        if (this.isStreaming) {
            this.stopQuoteStream();
        }

        this._lastParams = params;
        this._emitStatus('streaming', 'Starting quote stream...');

        // Build stream request
        const streamRequest = {
            swap: {
                inputMint: this._pubkeyToBytes(params.inputMint),
                outputMint: this._pubkeyToBytes(params.outputMint),
                amount: params.amount,
                swapMode: params.swapMode === 'ExactOut' ? 'ExactOut' : 'ExactIn',
                slippageBps: params.slippageBps || 50,
                dexes: params.dexes || [],
                excludeDexes: params.excludeDexes || [],
                onlyDirectRoutes: params.onlyDirectRoutes || false
            },
            transaction: {
                userPublicKey: params.userPublicKey
                    ? this._pubkeyToBytes(params.userPublicKey)
                    : new Uint8Array(32) // Empty pubkey if not provided
            },
            update: {
                intervalMs: params.intervalMs || 1000,
                num_quotes: params.numQuotes || 3
            }
        };

        // Send NewSwapQuoteStream request
        this._sendRequest('NewSwapQuoteStream', streamRequest)
            .then(response => {
                this._streamId = response.streamId;
                this.isStreaming = true;
                this._emitStatus('streaming', 'Quote stream active');
            })
            .catch(error => {
                this._emitError(error);
                this._emitStatus('error', error.message);
            });

        return true;
    }

    stopQuoteStream() {
        if (!this.isStreaming || !this._streamId) {
            this.isStreaming = false;
            return;
        }

        // Send stop request
        this._sendRequest('StopStream', { streamId: this._streamId })
            .then(() => {
                this.isStreaming = false;
                this._streamId = null;
                this._emitStatus('stopped', 'Quote stream stopped');
            })
            .catch(error => {
                console.warn('Error stopping stream:', error);
                // Force stop anyway
                this.isStreaming = false;
                this._streamId = null;
            });

        this._lastParams = null;
    }

    async disconnect() {
        this.stopQuoteStream();

        if (this._ws) {
            this._ws.close();
            this._ws = null;
        }

        this.isConnected = false;
        this._emitStatus('disconnected', 'Titan Exchange disconnected');
    }

    async executeSwap(quote, wallet) {
        if (!wallet || !wallet.publicKey) {
            throw new Error('Wallet not connected');
        }

        if (!quote.transaction) {
            throw new Error('Quote does not contain a pre-built transaction');
        }

        this._emitStatus('swapping', 'Preparing swap transaction...');

        try {
            // Deserialize the pre-built transaction
            const transaction = VersionedTransaction.deserialize(
                quote.transaction instanceof Uint8Array
                    ? quote.transaction
                    : new Uint8Array(Object.values(quote.transaction))
            );

            this._emitStatus('signing', 'Please sign the transaction...');

            // Sign with wallet
            const signedTransaction = await wallet.signTransaction(transaction);

            this._emitStatus('sending', 'Sending transaction...');

            // Send transaction
            const rawTransaction = signedTransaction.serialize();
            const signature = await this.connection.sendRawTransaction(rawTransaction, {
                skipPreflight: true,
                maxRetries: 2
            });

            this._emitStatus('confirming', 'Confirming transaction...');

            // Confirm transaction
            const confirmation = await this.connection.confirmTransaction(signature, 'confirmed');

            if (confirmation.value.err) {
                throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
            }

            this._emitStatus('success', 'Swap completed!');

            return {
                success: true,
                signature,
                inputAmount: quote.inputAmount,
                outputAmount: quote.outputAmount
            };

        } catch (error) {
            this._emitError(error);
            this._emitStatus('error', error.message);
            throw error;
        }
    }

    supportsStreaming() {
        return true;
    }
}

export default TitanSwapProvider;
